import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiEdit, FiTrash2, FiGift, FiStar } = FiIcons;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  }
};

const Rewards = () => {
  const { rewards, addReward, updateReward, deleteReward, standardRewards } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'Gift',
    streakLength: ''
  });

  const rewardTypes = [
    'Gift Card',
    'Special Outing', 
    'Toy',
    'Screen Time',
    'Special Time',
    'Gift',
    'Other'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.streakLength) return;
    
    const rewardData = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      type: formData.type,
      streakLength: parseInt(formData.streakLength)
    };
    
    if (editingReward) {
      updateReward(editingReward.id, rewardData);
    } else {
      addReward(rewardData);
    }
    
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'Gift',
      streakLength: ''
    });
    setShowForm(false);
    setEditingReward(null);
  };

  const handleEdit = (reward) => {
    setFormData({
      name: reward.name,
      description: reward.description || '',
      type: reward.type,
      streakLength: reward.streakLength.toString()
    });
    setEditingReward(reward);
    setShowForm(true);
  };

  const handleDelete = (rewardId) => {
    if (window.confirm('Are you sure you want to delete this reward? This will also remove it from any milestones.')) {
      deleteReward(rewardId);
    }
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      'Entertainment': '🎬',
      'Food': '🍽️',
      'Gaming': '🎮',
      'Outdoor': '🌳',
      'Games': '🎲',
      'Bedtime': '🌙'
    };
    return iconMap[category] || '🎁';
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">Rewards</h1>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowForm(!showForm)}
          className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
        >
          <SafeIcon icon={FiPlus} />
          <span>Add Custom Reward</span>
        </motion.button>
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            {editingReward ? 'Edit Reward' : 'Add New Custom Reward'}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reward Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="e.g., Extra Story Time"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                rows="3"
                placeholder="Describe the reward..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {rewardTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Suggested Streak Length *
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={formData.streakLength}
                  onChange={(e) => setFormData(prev => ({ ...prev, streakLength: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="7"
                  required
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                {editingReward ? 'Update Reward' : 'Add Reward'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Standard Rewards Section */}
      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <SafeIcon icon={FiStar} className="text-2xl text-amber-500" />
          <h2 className="text-2xl font-bold text-gray-800">Standard Rewards</h2>
          <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-medium">
            Built-in
          </span>
        </div>
        <p className="text-gray-600">
          These are pre-made rewards that work great for most families. They cannot be edited or deleted.
        </p>
        
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          {standardRewards.map((reward) => (
            <motion.div
              key={reward.id}
              variants={cardVariants}
              whileHover={{ scale: 1.02 }}
              className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl shadow-sm border border-amber-200 p-5 hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="text-2xl">{getCategoryIcon(reward.category)}</div>
                <span className="bg-amber-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                  Standard
                </span>
              </div>

              <h3 className="font-semibold text-lg text-gray-800 mb-2">{reward.name}</h3>
              
              {reward.description && (
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">{reward.description}</p>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Category:</span>
                  <span className="font-medium text-gray-700">{reward.category}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Type:</span>
                  <span className="font-medium text-gray-700">{reward.type}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Suggested streak:</span>
                  <span className="font-medium text-amber-600">{reward.streakLength} nights</span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* My Rewards Section */}
      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <SafeIcon icon={FiGift} className="text-2xl text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-800">My Custom Rewards</h2>
          <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium">
            {rewards.length} created
          </span>
        </div>
        <p className="text-gray-600">
          Create personalized rewards that work best for your family. You can edit or delete these anytime.
        </p>

        {rewards.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 bg-gray-50 rounded-xl"
          >
            <SafeIcon icon={FiGift} className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No custom rewards created yet</h3>
            <p className="text-gray-500 mb-6">
              Create personalized rewards to motivate your children's sleep routines!
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center space-x-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
            >
              <SafeIcon icon={FiPlus} />
              <span>Create Your First Reward</span>
            </button>
          </motion.div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
          >
            {rewards.map((reward) => (
              <motion.div
                key={reward.id}
                variants={cardVariants}
                whileHover={{ scale: 1.02 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <SafeIcon icon={FiGift} className="text-2xl text-purple-600" />
                  <div className="flex space-x-1">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleEdit(reward)}
                      className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                    >
                      <SafeIcon icon={FiEdit} />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleDelete(reward.id)}
                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <SafeIcon icon={FiTrash2} />
                    </motion.button>
                  </div>
                </div>

                <h3 className="font-semibold text-lg text-gray-800 mb-1">{reward.name}</h3>
                
                {reward.description && (
                  <p className="text-gray-600 text-sm mb-3">{reward.description}</p>
                )}

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Type:</span>
                    <span className="font-medium text-gray-700">{reward.type}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Suggested streak:</span>
                    <span className="font-medium text-purple-600">{reward.streakLength} nights</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Rewards;